import React from 'react';
import Icon from '../../../components/AppIcon';

const HowItWorksSection = () => {
  const steps = [
    {
      number: '01',
      title: 'Capture Data',
      description: 'Patients use the app to log symptoms and play games',
      details: 'Our intuitive interface makes it easy for patients to record daily symptoms, complete cognitive assessments, and sync data from wearable devices.',
      icon: 'Smartphone',
      color: 'primary'
    },
    {
      number: '02',
      title: 'AI Analysis',
      description: 'AI models analyze data to find patterns',
      details: 'Advanced machine learning algorithms process the collected data to identify trends, correlations, and anomalies that might not be visible to the human eye.',
      icon: 'Brain',
      color: 'warning'
    },
    {
      number: '03',
      title: 'Personalized Insights',
      description: 'Providers receive clear summaries to guide care',
      details: 'Healthcare providers get comprehensive, easy-to-understand reports with actionable recommendations to inform treatment decisions and patient care strategies.',
      icon: 'FileText',
      color: 'accent'
    }
  ];

  return (
    <section id="how-it-works" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
              How It Works
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Our three-step process transforms raw health data into meaningful insights 
              that enhance patient-provider communication and improve care outcomes.
            </p>
          </div>

          {/* Process Flow */}
          <div className="relative">
            {/* Connection Lines - Desktop */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-warning to-accent transform -translate-y-1/2 z-0" />

            {/* Steps */}
            <div className="grid lg:grid-cols-3 gap-8 relative z-10">
              {steps?.map((step, index) => (
                <div key={index} className="text-center group">
                  {/* Step Card */}
                  <div className="bg-card border border-border rounded-2xl p-8 hover:shadow-lg transition-all duration-300 hover:border-primary/20 relative">
                    {/* Step Number */}
                    <div className={`w-16 h-16 bg-${step?.color}/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <span className={`text-2xl font-bold text-${step?.color}`}>
                        {step?.number}
                      </span>
                    </div>

                    {/* Icon */}
                    <div className={`w-12 h-12 bg-${step?.color}/10 rounded-lg flex items-center justify-center mx-auto mb-4`}>
                      <Icon 
                        name={step?.icon} 
                        size={24} 
                        className={`text-${step?.color}`}
                      />
                    </div>

                    {/* Content */}
                    <h3 className="text-xl font-semibold text-card-foreground mb-3 group-hover:text-primary transition-colors">
                      {step?.title}
                    </h3>
                    <p className="text-muted-foreground font-medium mb-4">
                      {step?.description}
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {step?.details}
                    </p>

                    {/* Connector Arrow - Mobile */}
                    {index < steps?.length - 1 && (
                      <div className="lg:hidden flex justify-center mt-6">
                        <Icon name="ArrowDown" size={20} className="text-muted-foreground/50" />
                      </div>
                    )}
                  </div>

                  {/* Connector Arrow - Desktop */}
                  {index < steps?.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-20">
                      <div className="w-8 h-8 bg-background border border-border rounded-full flex items-center justify-center">
                        <Icon name="ArrowRight" size={16} className="text-muted-foreground" />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-16">
            <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-2xl p-8">
              <h3 className="text-2xl font-semibold text-foreground mb-4">
                See the Process in Action
              </h3>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Experience how MycoMind transforms health tracking into meaningful insights 
                that improve patient care and provider decision-making.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium">
                  <Icon name="Play" size={16} className="mr-2" />
                  Watch Demo
                </button>
                <button className="inline-flex items-center px-6 py-3 border border-border text-foreground rounded-lg hover:bg-muted transition-colors font-medium">
                  <Icon name="FileText" size={16} className="mr-2" />
                  Learn More
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;